import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import MaterialReactTable from 'material-react-table'
import { MRT_Localization_PT } from 'material-react-table/locales/pt'
import LinkIcon from '@mui/icons-material/Link'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from 'reactstrap'
import Breadcrumbs from '../../components/Common/Breadcrumb'
import Select from '../../components/Template/Input/Select'
import { getRegs, insert, update } from '../../utils/fc'
import { notifyError, notifySuccess } from '../../utils/notify'
import endPoints1Api from '../../services/end_points_1'

const TIPO_OPTIONS = [
  { value: 'formulario', label: 'Formulário' },
  { value: 'listagem', label: 'Listagem' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'modal', label: 'Modal' },
  { value: 'detalhe', label: 'Detalhe' },
]

const EMPTY_FORM = {
  tela_id: null,
  tela_hand: '',
  tela_nome: '',
  tela_desc: '',
  tela_contexto: '',
  tela_tipo: null,
  tela_rota: '',
  tela_nivelPermissaoMax: '',
  tela_componenteRaiz: null,
  componentes: [],
  tabelas: [],
}

const EMPTY_COMPONENTE_FORM = {
  componente_hand: '',
  componente_nome: '',
  componente_tipo: null,
  componente_origem: null,
  componente_arquivo: '',
  componente_import: '',
  componente_desc: '',
}

const COMPONENTE_TIPO_OPTIONS = [
  { value: 'input', label: 'Input' },
  { value: 'select', label: 'Select' },
  { value: 'tabela', label: 'Tabela' },
  { value: 'modal', label: 'Modal' },
  { value: 'formulario', label: 'Formulário' },
  { value: 'upload', label: 'Upload' },
  { value: 'button', label: 'Button' },
]

const COMPONENTE_ORIGEM_OPTIONS = [
  { value: 'interno', label: 'Interno' },
  { value: 'Reactstrap', label: 'Reactstrap' },
  { value: 'MUI', label: 'MUI' },
  { value: 'MRT', label: 'MRT' },
  { value: 'react-select', label: 'react-select' },
]

const getRotaHref = (rota) => {
  const value = String(rota || '').trim()
  if (!value) return null
  if (/^https?:\/\//i.test(value)) return value
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return null
  return value.startsWith('/') ? value : `/${value}`
}

const FLAG_FIELDS = [
  { key: 'telaTabela_select', label: 'Select' },
  { key: 'telaTabela_insert', label: 'Insert' },
  { key: 'telaTabela_update', label: 'Update' },
  { key: 'telaTabela_delete', label: 'Delete' },
  { key: 'telaTabela_principal', label: 'Principal' },
]

const mrtCompactProps = {
  muiTableBodyCellProps: { sx: { fontSize: '11px' } },
  muiTableHeadCellProps: {
    sx: {
      '.MuiSvgIcon-root': { fontSize: '18px' },
      fontSize: '12px',
      fontWeight: 'normal',
      padding: '8px',
    },
  },
  muiTablePaginationProps: {
    sx: {
      '.MuiTablePagination-displayedRows': { fontSize: '12px' },
      marginTop: '0px',
      fontSize: '12px',
    },
    labelRowsPerPage: <span style={{ fontSize: '12px' }}>Linhas por página</span>,
    rowsPerPageOptions: [10, 31, 50],
  },
  enableDensityToggle: false,
  enableColumnFilters: false,
  positionPagination: 'bottom',
}

const isActive = value => Number(value) === 1

const toOption = (id, label) => (
  id === undefined || id === null
    ? null
    : { value: Number(id), label: label || String(id) }
)

const tipoOption = value => TIPO_OPTIONS.find(opt => opt.value === value) || null

const RegistroTelas = () => {
  const user = useSelector(state => state.auth.user)
  const allowed = user && Number(user.perm_id) === 1
  const pfId = user?.pf_id

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [telas, setTelas] = useState([])
  const [componentes, setComponentes] = useState([])
  const [tabelaOptions, setTabelaOptions] = useState([])
  const [relations, setRelations] = useState({})
  const [expanded, setExpanded] = useState({})
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [componenteModalOpen, setComponenteModalOpen] = useState(false)
  const [componenteForm, setComponenteForm] = useState(EMPTY_COMPONENTE_FORM)

  const loadCatalog = useCallback(async () => {
    setLoading(true)
    try {
      const [telasRes, componentesRes] = await Promise.all([
        getRegs('tela'),
        getRegs('componente'),
      ])
      setTelas(Array.isArray(telasRes) ? telasRes : [])
      setComponentes(Array.isArray(componentesRes) ? componentesRes : [])
      try {
        const tablesRes = await endPoints1Api.post(
          '/',
          { acao: 'listar_tabelas' },
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Accept: 'multipart/form-data',
            },
          },
        )
        const rows = tablesRes?.data?.data
        setTabelaOptions(
          (Array.isArray(rows) ? rows : [])
            .filter(item => item && item.nome)
            .map(item => ({
              value: item.nome,
              label: item.schema ? `${item.schema}.${item.nome}` : item.nome,
              tabelaBanco_id: item.tabelaBanco_id || null,
              schema: item.schema || '',
              nome: item.nome,
            })),
        )
      } catch (tableError) {
        notifyError('Não foi possível carregar as tabelas do banco.')
        setTabelaOptions([])
      }
    } catch (e) {
      notifyError('Não foi possível carregar o catálogo de telas.')
      setTelas([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (allowed) loadCatalog()
  }, [allowed, loadCatalog])

  const telaOptions = useMemo(
    () => telas
      .map(item => toOption(item.tela_id, item.tela_nome || item.tela_hand))
      .filter(Boolean),
    [telas],
  )

  const componenteOptions = useMemo(
    () => componentes
      .map(item => toOption(item.componente_id, item.componente_nome || item.componente_hand))
      .filter(Boolean),
    [componentes],
  )

  const loadRelations = useCallback(async (telaId) => {
    if (!telaId) return { componentes: [], tabelas: [] }
    try {
      const [comps, tabs] = await Promise.all([
        getRegs('telaComponente', { tela_id: telaId }),
        getRegs('telaTabela', { tela_id: telaId }, {
          '(SELECT tabelaBanco_nome FROM tabelaBanco WHERE tabelaBanco.tabelaBanco_id = telaTabela.tabelaBanco_id)': 'tabelaBanco_nome',
          '(SELECT tabelaBanco_schema FROM tabelaBanco WHERE tabelaBanco.tabelaBanco_id = telaTabela.tabelaBanco_id)': 'tabelaBanco_schema',
        }),
      ])
      const payload = {
        componentes: Array.isArray(comps) ? comps.filter(row => isActive(row.telaComponente_ativo)) : [],
        tabelas: Array.isArray(tabs) ? tabs.filter(row => isActive(row.telaTabela_ativo)) : [],
      }
      setRelations(prev => ({ ...prev, [telaId]: payload }))
      return payload
    } catch (e) {
      notifyError('Não foi possível carregar o detalhe da tela.')
      return { componentes: [], tabelas: [] }
    }
  }, [])

  const openCreate = () => {
    setForm(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = async (tela) => {
    const rel = (relations[tela.tela_id] ? relations[tela.tela_id] : await loadRelations(tela.tela_id))
    const componentes = (rel?.componentes || [])
      .map((row) => {
        const opt = componenteOptions.find(item => Number(item.value) === Number(row.componente_id))
        if (!opt) return null
        return {
          ...opt,
          telaComponente_id: row.telaComponente_id,
          telaComponente_contexto: row.telaComponente_contexto || '',
        }
      })
      .filter(Boolean)
    const tabelas = (rel?.tabelas || []).map((row) => {
      const opt = tabelaOptions.find(item => (
        (item.tabelaBanco_id && Number(item.tabelaBanco_id) === Number(row.tabelaBanco_id))
        || String(item.nome) === String(row.tabelaBanco_nome)
        || String(item.value) === String(row.tabelaBanco_nome)
      ))
      return {
        value: opt?.value || row.tabelaBanco_nome || row.tabelaBanco_id,
        label: opt?.label || row.tabelaBanco_nome || String(row.tabelaBanco_id),
        tabelaBanco_id: row.tabelaBanco_id,
        schema: opt?.schema || row.tabelaBanco_schema || '',
        nome: opt?.nome || row.tabelaBanco_nome || '',
        telaTabela_id: row.telaTabela_id,
        telaTabela_select: Number(row.telaTabela_select) || 0,
        telaTabela_insert: Number(row.telaTabela_insert) || 0,
        telaTabela_update: Number(row.telaTabela_update) || 0,
        telaTabela_delete: Number(row.telaTabela_delete) || 0,
        telaTabela_principal: Number(row.telaTabela_principal) || 0,
        telaTabela_contexto: row.telaTabela_contexto || '',
      }
    })
    setForm({
      tela_id: tela.tela_id,
      tela_hand: tela.tela_hand || '',
      tela_nome: tela.tela_nome || '',
      tela_desc: tela.tela_desc || '',
      tela_contexto: tela.tela_contexto || '',
      tela_tipo: tipoOption(tela.tela_tipo),
      tela_rota: tela.tela_rota || '',
      tela_nivelPermissaoMax: tela.tela_nivelPermissaoMax ?? '',
      tela_componenteRaiz: telaOptions.find(opt => String(opt.value) === String(tela.tela_componenteRaiz)) || null,
      componentes,
      tabelas,
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    if (saving) return
    setModalOpen(false)
    setForm(EMPTY_FORM)
  }

  const openComponenteCreate = () => {
    setComponenteForm(EMPTY_COMPONENTE_FORM)
    setComponenteModalOpen(true)
  }

  const closeComponenteModal = () => {
    if (saving) return
    setComponenteModalOpen(false)
    setComponenteForm(EMPTY_COMPONENTE_FORM)
  }

  const setField = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const setComponenteField = (name, value) => {
    setComponenteForm(prev => ({ ...prev, [name]: value }))
  }

  const handleComponentesChange = (selected) => {
    const next = (selected || []).map((opt) => {
      const current = form.componentes.find(item => Number(item.value) === Number(opt.value))
      return current || {
        ...opt,
        telaComponente_contexto: '',
      }
    })
    setField('componentes', next)
  }

  const setTelaComponenteContexto = (componenteId, value) => {
    setForm(prev => ({
      ...prev,
      componentes: prev.componentes.map(item => (
        Number(item.value) === Number(componenteId)
          ? { ...item, telaComponente_contexto: value }
          : item
      )),
    }))
  }

  const handleTabelasChange = (selected) => {
    const next = (selected || []).map((opt) => {
      const current = form.tabelas.find(item => String(item.value) === String(opt.value))
      return current || {
        ...opt,
        telaTabela_select: 0,
        telaTabela_insert: 0,
        telaTabela_update: 0,
        telaTabela_delete: 0,
        telaTabela_principal: 0,
        telaTabela_contexto: '',
      }
    })
    setField('tabelas', next)
  }

  const setTabelaFlag = (tabelaId, key, checked) => {
    setForm(prev => ({
      ...prev,
      tabelas: prev.tabelas.map(item => (
        String(item.value) === String(tabelaId)
          ? { ...item, [key]: checked ? 1 : 0 }
          : item
      )),
    }))
  }

  const setTabelaContexto = (tabelaId, value) => {
    setForm(prev => ({
      ...prev,
      tabelas: prev.tabelas.map(item => (
        String(item.value) === String(tabelaId)
          ? { ...item, telaTabela_contexto: value }
          : item
      )),
    }))
  }

  const validate = () => {
    const hand = String(form.tela_hand || '').trim()
    const nome = String(form.tela_nome || '').trim()
    if (!hand) {
      notifyError('O identificador interno é obrigatório.')
      return false
    }
    if (!nome) {
      notifyError('O nome é obrigatório.')
      return false
    }
    const duplicated = telas.some(item => (
      String(item.tela_hand).toLowerCase() === hand.toLowerCase()
      && Number(item.tela_id) !== Number(form.tela_id)
    ))
    if (duplicated) {
      notifyError('Já existe uma tela com esse identificador interno.')
      return false
    }
    if (!form.componentes.length && !form.tabelas.length) {
      notifyError('Informe pelo menos um componente ou uma tabela.')
      return false
    }
    return true
  }

  const validateComponente = async () => {
    const hand = String(componenteForm.componente_hand || '').trim()
    const nome = String(componenteForm.componente_nome || '').trim()
    if (!hand) {
      notifyError('O identificador interno é obrigatório.')
      return { ok: false }
    }
    if (!nome) {
      notifyError('O nome é obrigatório.')
      return { ok: false }
    }
    const existing = (await getRegs('componente', {}, {}, false)) || []
    const found = existing.find(item => (
      String(item.componente_hand).toLowerCase() === hand.toLowerCase()
    ))
    if (found && isActive(found.componente_ativo)) {
      notifyError('Já existe um componente com esse identificador interno.')
      return { ok: false }
    }
    return { ok: true, hand, nome, found }
  }

  const ensureTabelaBancoId = async (option) => {
    if (option.tabelaBanco_id) return Number(option.tabelaBanco_id)
    const nome = option.nome || option.label || option.value
    const schema = option.schema || 'oikos'
    const existing = (await getRegs('tabelaBanco', { tabelaBanco_nome: nome }, {}, false)) || []
    const found = existing.find(row => (
      String(row.tabelaBanco_nome) === String(nome)
      && isActive(row.tabelaBanco_ativo)
    ))
    if (found) return Number(found.tabelaBanco_id)
    const inactive = existing.find(row => String(row.tabelaBanco_nome) === String(nome))
    if (inactive) {
      await update('tabelaBanco', inactive.tabelaBanco_id, {
        tabelaBanco_ativo: 1,
        tabelaBanco_usuarioMod: pfId,
      })
      return Number(inactive.tabelaBanco_id)
    }
    return insert('tabelaBanco', {
      tabelaBanco_schema: schema,
      tabelaBanco_nome: nome,
      tabelaBanco_usuarioCria: pfId,
      tabelaBanco_usuarioMod: pfId,
    })
  }

  const syncComponentes = async (telaId, selected) => {
    const existing = (await getRegs('telaComponente', { tela_id: telaId }, {}, false)) || []
    const remaining = new Map((selected || []).map(item => [Number(item.value), item]))
    await Promise.all(existing.map(async (row) => {
      const cid = Number(row.componente_id)
      const current = remaining.get(cid)
      if (current) {
        remaining.delete(cid)
        await update('telaComponente', row.telaComponente_id, {
          telaComponente_ativo: 1,
          telaComponente_contexto: String(current.telaComponente_contexto || '').trim() || null,
          telaComponente_usuarioMod: pfId,
        })
        return
      }
      if (isActive(row.telaComponente_ativo)) {
        await update('telaComponente', row.telaComponente_id, {
          telaComponente_ativo: 0,
          telaComponente_usuarioMod: pfId,
        })
      }
    }))
    await Promise.all([...remaining.values()].map(item => insert('telaComponente', {
      tela_id: telaId,
      componente_id: Number(item.value),
      telaComponente_contexto: String(item.telaComponente_contexto || '').trim() || null,
      telaComponente_usuarioCria: pfId,
      telaComponente_usuarioMod: pfId,
    })))
  }

  const syncTabelas = async (telaId, selected) => {
    const existing = (await getRegs('telaTabela', { tela_id: telaId }, {}, false)) || []
    const selectedWithIds = await Promise.all(selected.map(async (item) => ({
      ...item,
      tabelaBanco_id: await ensureTabelaBancoId(item),
    })))
    const remaining = new Map(selectedWithIds.map(item => [Number(item.tabelaBanco_id), item]))
    await Promise.all(existing.map(async (row) => {
      const tid = Number(row.tabelaBanco_id)
      const current = remaining.get(tid)
      if (current) {
        remaining.delete(tid)
        await update('telaTabela', row.telaTabela_id, {
          telaTabela_ativo: 1,
          telaTabela_select: Number(current.telaTabela_select) || 0,
          telaTabela_insert: Number(current.telaTabela_insert) || 0,
          telaTabela_update: Number(current.telaTabela_update) || 0,
          telaTabela_delete: Number(current.telaTabela_delete) || 0,
          telaTabela_principal: Number(current.telaTabela_principal) || 0,
          telaTabela_contexto: String(current.telaTabela_contexto || '').trim() || null,
          telaTabela_usuarioMod: pfId,
        })
        return
      }
      if (isActive(row.telaTabela_ativo)) {
        await update('telaTabela', row.telaTabela_id, {
          telaTabela_ativo: 0,
          telaTabela_usuarioMod: pfId,
        })
      }
    }))
    await Promise.all([...remaining.values()].map(item => insert('telaTabela', {
      tela_id: telaId,
      tabelaBanco_id: item.tabelaBanco_id,
      telaTabela_select: Number(item.telaTabela_select) || 0,
      telaTabela_insert: Number(item.telaTabela_insert) || 0,
      telaTabela_update: Number(item.telaTabela_update) || 0,
      telaTabela_delete: Number(item.telaTabela_delete) || 0,
      telaTabela_principal: Number(item.telaTabela_principal) || 0,
      telaTabela_contexto: String(item.telaTabela_contexto || '').trim() || null,
      telaTabela_usuarioCria: pfId,
      telaTabela_usuarioMod: pfId,
    })))
  }

  const handleSave = async () => {
    if (!validate()) return
    setSaving(true)
    try {
      const payload = {
        tela_hand: String(form.tela_hand).trim(),
        tela_nome: String(form.tela_nome).trim(),
        tela_desc: form.tela_desc || null,
        tela_contexto: form.tela_contexto || null,
        tela_tipo: form.tela_tipo?.value || null,
        tela_rota: form.tela_rota || null,
        tela_nivelPermissaoMax: form.tela_nivelPermissaoMax === ''
          ? null
          : Number(form.tela_nivelPermissaoMax),
        tela_componenteRaiz: form.tela_componenteRaiz?.value != null
          ? String(form.tela_componenteRaiz.value)
          : null,
        tela_usuarioMod: pfId,
      }
      let telaId = form.tela_id
      if (telaId) {
        const updated = await update('tela', telaId, payload)
        if (!updated) throw new Error('update')
      } else {
        telaId = await insert('tela', {
          ...payload,
          tela_usuarioCria: pfId,
        })
        if (!telaId) throw new Error('insert')
      }
      await syncComponentes(telaId, form.componentes)
      await syncTabelas(telaId, form.tabelas)
      notifySuccess(form.tela_id ? 'Tela atualizada com sucesso.' : 'Tela documentada com sucesso.')
      setModalOpen(false)
      setForm(EMPTY_FORM)
      setRelations({})
      await loadCatalog()
    } catch (e) {
      notifyError('Não foi possível gravar a tela.')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveComponente = async () => {
    const check = await validateComponente()
    if (!check.ok) return
    setSaving(true)
    try {
      const payload = {
        componente_hand: check.hand,
        componente_nome: check.nome,
        componente_tipo: componenteForm.componente_tipo?.value || null,
        componente_origem: componenteForm.componente_origem?.value || null,
        componente_arquivo: String(componenteForm.componente_arquivo || '').trim() || null,
        componente_import: String(componenteForm.componente_import || '').trim() || null,
        componente_desc: String(componenteForm.componente_desc || '').trim() || null,
        componente_ativo: 1,
        componente_usuarioMod: pfId,
      }
      if (check.found) {
        const updated = await update('componente', check.found.componente_id, payload)
        if (!updated) throw new Error('update')
      } else {
        const id = await insert('componente', {
          ...payload,
          componente_usuarioCria: pfId,
        })
        if (!id) throw new Error('insert')
      }
      notifySuccess('Componente documentado com sucesso.')
      setComponenteModalOpen(false)
      setComponenteForm(EMPTY_COMPONENTE_FORM)
      const componentesRes = await getRegs('componente')
      setComponentes(Array.isArray(componentesRes) ? componentesRes : [])
    } catch (e) {
      notifyError('Não foi possível gravar o componente.')
    } finally {
      setSaving(false)
    }
  }

  const handleInativar = async (tela) => {
    try {
      const updated = await update('tela', tela.tela_id, {
        tela_ativo: 0,
        tela_usuarioMod: pfId,
      })
      if (!updated) throw new Error('update')
      notifySuccess('Tela inativada com sucesso.')
      setRelations({})
      await loadCatalog()
    } catch (e) {
      notifyError('Não foi possível inativar a tela.')
    }
  }

  const columns = useMemo(() => ([
    { accessorKey: 'tela_hand', header: 'Identificador interno' },
    { accessorKey: 'tela_nome', header: 'Nome' },
    { accessorKey: 'tela_tipo', header: 'Tipo' },
    {
      accessorKey: 'tela_rota',
      header: 'Rota',
      Cell: ({ cell }) => {
        const rota = cell.getValue()
        const href = getRotaHref(rota)
        return (
          <span className="d-inline-flex align-items-center" style={{ gap: 6 }}>
            <span>{rota || '—'}</span>
            {href && (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title="Abrir rota em nova aba"
                aria-label="Abrir rota em nova aba"
                onClick={e => e.stopPropagation()}
              >
                <LinkIcon fontSize="small" />
              </a>
            )}
          </span>
        )
      },
    },
  ]), [])

  const renderDetailPanel = ({ row }) => {
    const tela = row.original
    const rel = relations[tela.tela_id]
    if (!rel) {
      return (
        <div className="p-3 text-center">
          <Spinner size="sm" color="primary" />
        </div>
      )
    }
    const raiz = telaOptions.find(opt => String(opt.value) === String(tela.tela_componenteRaiz))
    return (
      <div className="p-3" style={{ background: '#fafbfc' }}>
        <Row>
          <Col md={6}>
            <p className="mb-1">
              <strong>Descrição:</strong>
              {' '}
              {tela.tela_desc || '—'}
            </p>
            <p className="mb-1">
              <strong>Contexto de uso:</strong>
              {' '}
              {tela.tela_contexto || '—'}
            </p>
            <p className="mb-1">
              <strong>Nível máximo de permissão:</strong>
              {' '}
              {tela.tela_nivelPermissaoMax ?? '—'}
            </p>
            <p className="mb-1">
              <strong>Tela raiz:</strong>
              {' '}
              {raiz?.label || tela.tela_componenteRaiz || '—'}
            </p>
          </Col>
          <Col md={6}>
            <div className="mb-1">
              <strong>Componentes:</strong>
              {!rel.componentes.length && ' —'}
              {rel.componentes.map((item) => {
                const opt = componenteOptions.find(o => Number(o.value) === Number(item.componente_id))
                const contexto = String(item.telaComponente_contexto || '').trim()
                return (
                  <div key={item.telaComponente_id || item.componente_id} className="small">
                    {opt?.label || item.componente_id}
                    {contexto ? ` — ${contexto}` : ''}
                  </div>
                )
              })}
            </div>
            <div className="mb-1">
              <strong>Tabelas:</strong>
              {!rel.tabelas.length && ' —'}
              {rel.tabelas.map((tab) => {
                const opt = tabelaOptions.find(o => (
                  (o.tabelaBanco_id && Number(o.tabelaBanco_id) === Number(tab.tabelaBanco_id))
                  || String(o.nome) === String(tab.tabelaBanco_nome)
                  || String(o.value) === String(tab.tabelaBanco_nome)
                ))
                const flags = FLAG_FIELDS
                  .filter(flag => Number(tab[flag.key]) === 1)
                  .map(flag => flag.label)
                  .join(', ')
                const contexto = String(tab.telaTabela_contexto || '').trim()
                return (
                  <div key={tab.telaTabela_id || tab.tabelaBanco_id} className="small">
                    {opt?.label || tab.tabelaBanco_nome || tab.tabelaBanco_id}
                    {flags ? ` (${flags})` : ''}
                    {contexto ? ` — ${contexto}` : ''}
                  </div>
                )
              })}
            </div>
          </Col>
        </Row>
      </div>
    )
  }

  if (!allowed) return <></>

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Catálogo de telas"
          breadcrumbItems={[{ title: 'Catálogo de telas' }]}
        />

        <Row className="mb-3">
          <Col>
            <h4 className="mb-0">Telas documentadas</h4>
          </Col>
          <Col className="text-right">
            <div className="d-inline-flex" style={{ gap: 8 }}>
              <Button color="primary" type="button" onClick={openCreate}>
                Documentar tela
              </Button>
              <Button color="primary" outline type="button" onClick={openComponenteCreate}>
                Documentar componente
              </Button>
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card>
              <CardBody>
                {loading && (
                  <div className="d-flex align-items-center justify-content-center py-4">
                    <Spinner color="primary" />
                  </div>
                )}
                {!loading && (
                  <MaterialReactTable
                    columns={columns}
                    data={telas}
                    getRowId={row => String(row.tela_id)}
                    initialState={{
                      density: 'compact',
                      pagination: { pageIndex: 0, pageSize: 50 },
                    }}
                    enableExpanding
                    enableExpandAll={false}
                    renderDetailPanel={renderDetailPanel}
                    state={{ expanded }}
                    onExpandedChange={(updater) => {
                      setExpanded((prev) => {
                        const next = typeof updater === 'function' ? updater(prev) : updater
                        Object.entries(next || {}).forEach(([id, isOpen]) => {
                          if (isOpen) loadRelations(id)
                        })
                        return next
                      })
                    }}
                    enableRowActions
                    positionActionsColumn="last"
                    renderRowActions={({ row }) => (
                      <div className="d-flex" style={{ gap: 6 }}>
                        <Button
                          color="light"
                          size="sm"
                          type="button"
                          onClick={() => openEdit(row.original)}
                        >
                          Editar
                        </Button>
                        <Button
                          color="danger"
                          size="sm"
                          outline
                          type="button"
                          onClick={() => handleInativar(row.original)}
                        >
                          Inativar
                        </Button>
                      </div>
                    )}
                    enableGlobalFilter
                    enablePagination={telas.length > 10}
                    localization={MRT_Localization_PT}
                    {...mrtCompactProps}
                    muiTablePaperProps={{ sx: { boxShadow: 'none', border: '1px solid #eef0f3' } }}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={modalOpen} toggle={closeModal} centered size="lg">
          <ModalHeader toggle={closeModal}>
            {form.tela_id ? 'Editar tela' : 'Documentar tela'}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Identificador interno</Label>
                  <Input
                    type="text"
                    value={form.tela_hand}
                    onChange={e => setField('tela_hand', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Nome</Label>
                  <Input
                    type="text"
                    value={form.tela_nome}
                    onChange={e => setField('tela_nome', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label>Descrição</Label>
                  <Input
                    type="text"
                    value={form.tela_desc}
                    onChange={e => setField('tela_desc', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label>Contexto de uso</Label>
                  <Input
                    type="textarea"
                    rows="3"
                    value={form.tela_contexto}
                    onChange={e => setField('tela_contexto', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Tipo</Label>
                  <Select
                    options={TIPO_OPTIONS}
                    value={form.tela_tipo}
                    onChange={value => setField('tela_tipo', value)}
                    isClearable
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Rota</Label>
                  <Input
                    type="text"
                    value={form.tela_rota}
                    onChange={e => setField('tela_rota', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Nível máximo de permissão</Label>
                  <Input
                    type="number"
                    step="1"
                    value={form.tela_nivelPermissaoMax}
                    onChange={e => setField('tela_nivelPermissaoMax', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Tela raiz</Label>
                  <Select
                    options={telaOptions.filter(opt => Number(opt.value) !== Number(form.tela_id))}
                    value={form.tela_componenteRaiz}
                    onChange={value => setField('tela_componenteRaiz', value)}
                    isClearable
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label>Componentes</Label>
                  <Select
                    isMulti
                    options={componenteOptions}
                    value={form.componentes}
                    onChange={handleComponentesChange}
                  />
                </FormGroup>
              </Col>
              {form.componentes.map(componente => (
                <Col md={12} key={componente.value} className="mb-2">
                  <div className="border rounded p-2">
                    <div className="font-weight-bold mb-2">{componente.label}</div>
                    <FormGroup className="mb-0">
                      <Label>Contexto</Label>
                      <Input
                        type="textarea"
                        rows="2"
                        value={componente.telaComponente_contexto || ''}
                        onChange={e => setTelaComponenteContexto(componente.value, e.target.value)}
                      />
                    </FormGroup>
                  </div>
                </Col>
              ))}
              <Col md={12}>
                <FormGroup>
                  <Label>Tabelas</Label>
                  <Select
                    isMulti
                    options={tabelaOptions}
                    value={form.tabelas}
                    onChange={handleTabelasChange}
                  />
                </FormGroup>
              </Col>
              {form.tabelas.map(tabela => (
                <Col md={12} key={tabela.value} className="mb-2">
                  <div className="border rounded p-2">
                    <div className="font-weight-bold mb-2">{tabela.label}</div>
                    <div className="d-flex flex-wrap" style={{ gap: 16 }}>
                      {FLAG_FIELDS.map(flag => (
                        <FormGroup check key={flag.key} className="mb-0">
                          <Label check>
                            <Input
                              type="checkbox"
                              checked={Number(tabela[flag.key]) === 1}
                              onChange={e => setTabelaFlag(tabela.value, flag.key, e.target.checked)}
                            />
                            {' '}
                            {flag.label}
                          </Label>
                        </FormGroup>
                      ))}
                    </div>
                    <FormGroup className="mb-0 mt-2">
                      <Label>Contexto</Label>
                      <Input
                        type="textarea"
                        rows="2"
                        value={tabela.telaTabela_contexto || ''}
                        onChange={e => setTabelaContexto(tabela.value, e.target.value)}
                      />
                    </FormGroup>
                  </div>
                </Col>
              ))}
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={closeModal} disabled={saving}>
              Cancelar
            </Button>
            <Button color="primary" type="button" onClick={handleSave} disabled={saving}>
              Documentar
              {saving && <Spinner size="sm" className="ml-2" />}
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={componenteModalOpen} toggle={closeComponenteModal} centered size="lg">
          <ModalHeader toggle={closeComponenteModal}>
            Documentar componente
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Identificador interno</Label>
                  <Input
                    type="text"
                    value={componenteForm.componente_hand}
                    onChange={e => setComponenteField('componente_hand', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Nome</Label>
                  <Input
                    type="text"
                    value={componenteForm.componente_nome}
                    onChange={e => setComponenteField('componente_nome', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Tipo</Label>
                  <Select
                    options={COMPONENTE_TIPO_OPTIONS}
                    value={componenteForm.componente_tipo}
                    onChange={value => setComponenteField('componente_tipo', value)}
                    isClearable
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Origem</Label>
                  <Select
                    options={COMPONENTE_ORIGEM_OPTIONS}
                    value={componenteForm.componente_origem}
                    onChange={value => setComponenteField('componente_origem', value)}
                    isClearable
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Arquivo</Label>
                  <Input
                    type="text"
                    value={componenteForm.componente_arquivo}
                    onChange={e => setComponenteField('componente_arquivo', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Import</Label>
                  <Input
                    type="text"
                    value={componenteForm.componente_import}
                    onChange={e => setComponenteField('componente_import', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label>Descrição</Label>
                  <Input
                    type="textarea"
                    rows="3"
                    value={componenteForm.componente_desc}
                    onChange={e => setComponenteField('componente_desc', e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={closeComponenteModal} disabled={saving}>
              Cancelar
            </Button>
            <Button color="primary" type="button" onClick={handleSaveComponente} disabled={saving}>
              Documentar
              {saving && <Spinner size="sm" className="ml-2" />}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  )
}

export default RegistroTelas
